import type { MouseEvent } from 'react';
import { ArrowRight } from 'lucide-react';
import { formatNewsDate, getNewsCta } from '../utils/news.utils';
import { NewsBody } from './NewsBody';

interface NewsArticleProps {
  title: string;
  shortDescription: string;
  coverImageUrl?: string | null;
  /** Image montrée en entier, sans recadrage (mise à jour logicielle : texte dans les coins). */
  coverContain?: boolean;
  bodyHtml: string | null | undefined;
  ctaLabel?: string | null;
  ctaUrl?: string | null;
  /** Date de publication (ISO). Absente tant que la nouveauté est un brouillon. */
  publishedAt?: string | null;
  className?: string;
  /** Classes ajoutées au corps (la page de lecture y accroche la visionneuse d'images). */
  bodyClassName?: string;
  /**
   * Clic sur le bouton d'action. La page de lecture s'en sert pour déclarer le
   * clic et, pour un chemin interne, naviguer sans recharger l'application
   * (`event.preventDefault()`). Sans lui, le lien se comporte en lien ordinaire.
   */
  onCtaClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * Une nouveauté telle qu'elle se lit : couverture, titre, description courte,
 * corps, bouton d'action. Purement présentationnel — aucun appel réseau, aucune
 * dépendance à l'administration : sert d'aperçu dans le formulaire et peut être
 * repris tel quel par la page ou la modale de lecture.
 */
export function NewsArticle({
  title,
  shortDescription,
  coverImageUrl,
  coverContain = false,
  bodyHtml,
  ctaLabel,
  ctaUrl,
  publishedAt,
  className = '',
  bodyClassName,
  onCtaClick,
}: NewsArticleProps) {
  const cta = getNewsCta({ ctaLabel, ctaUrl });
  const publishedOn = formatNewsDate(publishedAt);

  return (
    <article className={className}>
      {coverImageUrl && (
        <img
          src={coverImageUrl}
          alt=""
          className={`tw-w-full tw-max-h-72 tw-rounded-lg tw-mb-4 ${
            coverContain ? 'tw-object-contain tw-bg-white' : 'tw-object-cover'
          }`}
        />
      )}

      {publishedOn && <p className="tw-text-xs tw-text-gray-500 tw-mb-1">{publishedOn}</p>}

      <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2 tw-break-words">{title}</h1>

      {shortDescription && (
        <p className="tw-text-base tw-text-gray-600 tw-mb-4 tw-whitespace-pre-line tw-break-words">
          {shortDescription}
        </p>
      )}

      <NewsBody html={bodyHtml} className={bodyClassName} />

      {cta && (
        <a
          href={cta.url}
          {...(cta.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          onClick={onCtaClick}
          className="tw-inline-flex tw-items-center tw-gap-2 tw-mt-5 tw-px-4 tw-py-2 tw-bg-affinicar-primary tw-text-white tw-rounded-lg tw-font-medium tw-text-sm tw-no-underline hover:tw-bg-affinicar-secondary hover:tw-text-white tw-transition-colors"
        >
          {cta.label}
          <ArrowRight className="tw-w-4 tw-h-4" />
        </a>
      )}
    </article>
  );
}
